// ScanPromptHero — the resting "SCAN X" prompt shown by every picking
// workflow (Single / Large / Pick & Sort / Shelf Drop / Forecast Drop)
// before an active pick target exists: starting-location scan, first /
// resume tote scan, and the transient between-phases fallback.
//
// Mirrors the wipe page's scan prompt (wipe.tsx / .wipe-scan-prompt-icon):
// a muted ScanBarcode glyph above full-size scansheet command words.
// Replaces the previous per-phase coloured module icon (MapPin /
// PackageOpen) + mid-size sub-prompt, so every scan-first resting state
// in the app reads the same way.
import { ScanBarcode } from "lucide-react";
import FitWord from "../FitWord";
import "../../styles/scansheet.css";

interface ScanPromptHeroProps {
    /** Already-translated prompt, e.g. t("drop_scan_current_location").
     *  Uppercased and split on whitespace into stacked command words
     *  (each FitWord scales itself down rather than wrap/clip). Empty
     *  string renders the glyph alone — used by the transient
     *  between-phases fallback that deliberately shows no copy. */
    prompt: string;
    /** True while the bad-scan ScanFlashOverlay is up — hides the hero
     *  so the red X reads against a clean red-washed page. */
    flashHidden?: boolean;
    /** Extra content below the command words (e.g. the amber
     *  wave-complete tick wrapper). */
    children?: React.ReactNode;
    /** Extra hero modifier class. Used where a caller adds content below
     *  the command words and needs the hero's generous top padding
     *  tightened so the addition doesn't get pushed off a handheld
     *  screen (see .picking-hero--with-cages). */
    className?: string;
    /** Extra content rendered ABOVE the scan glyph (e.g. Shelf Drop's
     *  units-by-aisle info button). */
    aboveIcon?: React.ReactNode;
}

const ScanPromptHero = ({ prompt, flashHidden, children, className, aboveIcon }: ScanPromptHeroProps) => (
    <div className={
        "picking-hero picking-hero--first-tote picking-hero--scan-prompt"
        + (flashHidden ? " scan-flash-hidden" : "")
        + (className ? " " + className : "")
    }>
        {aboveIcon}
        <ScanBarcode size={120} strokeWidth={1.2} className="picking-scan-prompt-icon" />
        <div className="scan-command">
            {prompt.toUpperCase().split(/\s+/).filter(Boolean).map((word, i) => (
                <FitWord key={i}>{word}</FitWord>
            ))}
        </div>
        {children}
    </div>
);

export default ScanPromptHero;
